import React, { useState, useEffect, useCallback } from 'react';
import {
  getPrincipaux, createPrincipal, updatePrincipal, deletePrincipal, getPrincipauxStats,
  getSecondaires, createSecondaire, updateSecondaire, deleteSecondaire,
  getDeces, createDeces, updateDeces, deleteDeces, getDecesStats, getDecesPaymentStatus, getAdherentsPaymentDetails, sendDecesNotificationSMS,
  getCotisations as getRestitutions, createCotisation as createRestitution, updateCotisation as updateRestitution, deleteCotisation as deleteRestitution, getCotisationsStats as getRestitutionsStats
} from '../../services/essieService';
import BroadcastSMS from '../ESSIE-EHOUN/BroadcastSMS';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../../styles/ARA.css';

const ESSIEModule = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    principaux: { totalPrincipaux: 0, totalSecondaires: 0, totalAdherents: 0 },
    deces: { totalDeces: 0, totalCollecte: 0, totalPrestations: 0, montantTotalPrestations: 0, availableYears: [] },
    restitutions: { totalCount: 0, totalAmount: 0 }
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Année en cours par défaut

  // Principaux
  const [principaux, setPrincipaux] = useState([]);
  const [principauxPagination, setPrincipauxPagination] = useState({ page: 1, totalPages: 1 });
  const [principalForm, setPrincipalForm] = useState({
    code: '', nom: '', date_adhesion: '', montant_adhesion: '', lieu_residence: '',
    contact: '', ayant_droit: '', contact_ayant_droit: '', actif: true
  });
  const [editingPrincipal, setEditingPrincipal] = useState(null);
  const [showPrincipalForm, setShowPrincipalForm] = useState(false);
  const [viewingPrincipalSecondaires, setViewingPrincipalSecondaires] = useState(null);

  // Secondaires
  const [secondaires, setSecondaires] = useState([]);
  const [secondairesPagination, setSecondairesPagination] = useState({ page: 1, totalPages: 1 });
  const [secondaireForm, setSecondaireForm] = useState({
    id_principal: '', nom: '', lieu_residence: '', lien_parente: '', date_adhesion: '', montant_adhesion: '', actif: true
  });
  const [editingSecondaire, setEditingSecondaire] = useState(null);
  const [showSecondaireForm, setShowSecondaireForm] = useState(false);
  const [principalSearch, setPrincipalSearch] = useState('');
  const [secondairesPrincipalFilter, setSecondairesPrincipalFilter] = useState('');

  // Deces
  const [decesList, setDecesList] = useState([]);
  const [decesPagination, setDecesPagination] = useState({ page: 1, totalPages: 1 });
  const [decesForm, setDecesForm] = useState({
    code_defunt: '', numero_cas: '', nom_defunt: '', date_deces: '', montant_par_tete: '', description: '',
    type_defunt: '', id_adherent: '', montant_prestation: '', beneficiaire: '', date_prestation: '', prestation_payee: false
  });
  const [editingDeces, setEditingDeces] = useState(null);
  const [showDecesForm, setShowDecesForm] = useState(false);
  const [selectedDeces, setSelectedDeces] = useState(null);
  const [decesPaymentStatus, setDecesPaymentStatus] = useState(null);
  const [adherentsPaymentDetails, setAdherentsPaymentDetails] = useState(null);
  const [expandedAdherent, setExpandedAdherent] = useState(null);
  const [prestationEligible, setPrestationEligible] = useState(true);

  // Restitutions
  const [restitutions, setRestitutions] = useState([]);
  const [restitutionsPagination, setRestitutionsPagination] = useState({ page: 1, totalPages: 1 });
  const [restitutionForm, setRestitutionForm] = useState({
    id_deces: '', id_principal: '', montant_total: '', nb_personnes: 1, date_paiement: ''
  });
  const [editingRestitution, setEditingRestitution] = useState(null);
  const [showRestitutionForm, setShowRestitutionForm] = useState(false);
  const [restitutionPrincipalSearch, setRestitutionPrincipalSearch] = useState('');
  const [montantDu, setMontantDu] = useState(0);
  const [montantDejaPayé, setMontantDejaPayé] = useState(0);
  const [resteAPayer, setResteAPayer] = useState(0);

  // Load dashboard
  const loadDashboard = useCallback(async (year = null) => {
    try {
      setLoading(true);
      const [principauxStats, decesStats, restitutionsStats] = await Promise.all([
        getPrincipauxStats(year),
        getDecesStats(year),
        getRestitutionsStats()
      ]);
      setDashboardStats({
        principaux: principauxStats,
        deces: decesStats,
        restitutions: restitutionsStats
      });
    } catch (err) {
      setError('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load principaux
  const loadPrincipaux = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const result = await getPrincipaux({ page, limit: 50 });
      setPrincipaux(result.data || []);
      setPrincipauxPagination({
        page: result.page || 1,
        totalPages: result.totalPages || 1
      });
    } catch (err) {
      setError('Erreur lors du chargement des adhérents principaux');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load secondaires
  const loadSecondaires = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const result = await getSecondaires({ page, limit: 50 });
      setSecondaires(result.data || []);
      setSecondairesPagination({
        page: result.pagination?.page || 1,
        totalPages: result.pagination?.totalPages || 1
      });
    } catch (err) {
      setError('Erreur lors du chargement des adhérents secondaires');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load deces
  const loadDeces = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const result = await getDeces({ page, limit: 50 });
      setDecesList(result.data || []);
      setDecesPagination({
        page: result.page || 1,
        totalPages: result.totalPages || 1
      });
    } catch (err) {
      setError('Erreur lors du chargement des décès');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load restitutions
  const loadRestitutions = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const result = await getRestitutions({ page, limit: 50 });
      setRestitutions(result.data || []);
      setRestitutionsPagination({
        page: result.pagination?.page || 1,
        totalPages: result.pagination?.totalPages || 1
      });
    } catch (err) {
      setError('Erreur lors du chargement des restitutions');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load payment status for a deces
  const loadDecesPaymentStatus = async (decesId) => {
    try {
      setLoading(true);
      const status = await getDecesPaymentStatus(decesId);
      setDecesPaymentStatus(status);
      setSelectedDeces(status.deces);
    } catch (err) {
      setError('Erreur lors du chargement du statut des paiements');
    } finally {
      setLoading(false);
    }
  };

  // Load all adherents payment details
  const loadAdherentsPaymentDetails = async () => {
    try {
      setLoading(true);
      const details = await getAdherentsPaymentDetails();
      setAdherentsPaymentDetails(details);
    } catch (err) {
      setError('Erreur lors du chargement des détails de paiement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') loadDashboard(selectedYear);
    else if (activeTab === 'principaux') { loadPrincipaux(); loadSecondaires(); }
    else if (activeTab === 'secondaires') { loadSecondaires(); loadPrincipaux(); }
    else if (activeTab === 'deces') { loadDeces(); loadPrincipaux(); loadSecondaires(); }
    else if (activeTab === 'restitutions') { loadRestitutions(); loadDeces(); loadPrincipaux(); }
    else if (activeTab === 'statut-paiements') { loadAdherentsPaymentDetails(); }
  }, [activeTab, selectedYear, loadDashboard, loadPrincipaux, loadSecondaires, loadDeces, loadRestitutions]);

  // Clear messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Handle Principal
  const handlePrincipalSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingPrincipal) {
        await updatePrincipal(editingPrincipal.id, principalForm);
        setSuccess('Adhérent principal mis à jour');
      } else {
        await createPrincipal(principalForm);
        setSuccess('Adhérent principal créé');
      }
      resetPrincipalForm();
      loadPrincipaux();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const editPrincipal = (principal) => {
    setPrincipalForm({
      code: principal.code || '',
      nom: principal.nom || '',
      date_adhesion: principal.date_adhesion ? principal.date_adhesion.split('T')[0] : '',
      montant_adhesion: principal.montant_adhesion || '',
      lieu_residence: principal.lieu_residence || '',
      contact: principal.contact || '',
      ayant_droit: principal.ayant_droit || '',
      contact_ayant_droit: principal.contact_ayant_droit || '',
      actif: principal.actif !== undefined ? principal.actif : true
    });
    setEditingPrincipal(principal);
    setShowPrincipalForm(true);
  };

  const handleDeletePrincipal = async (id) => {
    if (!window.confirm('Supprimer cet adhérent principal?')) return;
    try {
      await deletePrincipal(id);
      setSuccess('Adhérent supprimé');
      loadPrincipaux();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const resetPrincipalForm = () => {
    setPrincipalForm({
      code: '', nom: '', date_adhesion: '', montant_adhesion: '', lieu_residence: '',
      contact: '', ayant_droit: '', contact_ayant_droit: '', actif: true
    });
    setEditingPrincipal(null);
    setShowPrincipalForm(false);
  };

  // Handle Secondaire
  const handleSecondaireSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingSecondaire) {
        await updateSecondaire(editingSecondaire.id, secondaireForm);
        setSuccess('Adhérent secondaire mis à jour');
      } else {
        await createSecondaire(secondaireForm);
        setSuccess('Adhérent secondaire créé');
      }
      resetSecondaireForm();
      loadSecondaires();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const editSecondaire = (secondaire) => {
    setSecondaireForm({
      id_principal: secondaire.id_principal || '',
      nom: secondaire.nom || '',
      lieu_residence: secondaire.lieu_residence || '',
      montant_adhesion: secondaire.montant_adhesion || '',
      lien_parente: secondaire.lien_parente || '',
      date_adhesion: secondaire.date_adhesion ? secondaire.date_adhesion.split('T')[0] : '',
      actif: secondaire.actif !== undefined ? secondaire.actif : true
    });
    setEditingSecondaire(secondaire);
    setShowSecondaireForm(true);
  };

  const handleDeleteSecondaire = async (id) => {
    if (!window.confirm('Supprimer cet adhérent secondaire?')) return;
    try {
      await deleteSecondaire(id);
      setSuccess('Adhérent supprimé');
      loadSecondaires();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const resetSecondaireForm = () => {
    setSecondaireForm({
      id_principal: '', nom: '', lieu_residence: '', lien_parente: '', date_adhesion: '', montant_adhesion: '', actif: true
    });
    setEditingSecondaire(null);
    setShowSecondaireForm(false);
    setPrincipalSearch('');
  };

  // Filtrer les principaux pour la recherche
  const filteredPrincipaux = principaux.filter(p => {
    if (!principalSearch) return true;
    const search = principalSearch.toLowerCase();
    return (
      p.code.toLowerCase().includes(search) ||
      p.nom.toLowerCase().includes(search) ||
      (p.contact && p.contact.toLowerCase().includes(search))
    );
  });

  // Obtenir les secondaires d'un principal
  const getSecondairesDuPrincipal = (principalId) => {
    return secondaires.filter(s => s.id_principal === principalId);
  };

  // Handle Deces
  const handleDecesSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingDeces) {
        await updateDeces(editingDeces.id, decesForm);
        setSuccess('Décès mis à jour');
      } else {
        await createDeces(decesForm);
        setSuccess('Décès enregistré');
      }
      resetDecesForm();
      loadDeces();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const editDecesItem = (deces) => {
    setDecesForm({
      code_defunt: deces.code_defunt || '',
      numero_cas: deces.numero_cas || '',
      nom_defunt: deces.nom_defunt || '',
      date_deces: deces.date_deces ? deces.date_deces.split('T')[0] : '',
      montant_par_tete: deces.montant_par_tete || '',
      description: deces.description || '',
      type_defunt: deces.type_defunt || '',
      id_adherent: deces.id_adherent || '',
      montant_prestation: deces.montant_prestation || '',
      beneficiaire: deces.beneficiaire || '',
      date_prestation: deces.date_prestation ? deces.date_prestation.split('T')[0] : '',
      prestation_payee: deces.prestation_payee || false
    });
    setEditingDeces(deces);
    setShowDecesForm(true);
  };

  const handleDeleteDeces = async (id) => {
    if (!window.confirm('Supprimer ce décès?')) return;
    try {
      await deleteDeces(id);
      setSuccess('Décès supprimé');
      loadDeces();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleSendDecesSMS = async (deces) => {
    if (!window.confirm(`Envoyer un SMS de notification à tous les adhérents actifs pour le décès de ${deces.nom_defunt}?`)) return;
    try {
      setLoading(true);
      const result = await sendDecesNotificationSMS(deces.id);
      const testModeMsg = result.testMode ? ' (MODE TEST)' : '';
      setSuccess(`${result.summary.success} SMS envoyé(s) sur ${result.summary.total}${testModeMsg}`);
      if (result.summary.failed > 0) {
        setError(`${result.summary.failed} SMS ont échoué`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi des SMS');
    } finally {
      setLoading(false);
    }
  };

  const resetDecesForm = () => {
    setDecesForm({
      code_defunt: '', numero_cas: '', nom_defunt: '', date_deces: '', montant_par_tete: '', description: '',
      type_defunt: '', id_adherent: '', montant_prestation: '', beneficiaire: '', date_prestation: '', prestation_payee: false
    });
    setEditingDeces(null);
    setShowDecesForm(false);
    setPrestationEligible(true);
  };

  // Vérifier si éligible à la prestation (8 mois minimum)
  const checkPrestationEligibility = (dateAdhesion) => {
    if (!dateAdhesion) return false;
    const adhesion = new Date(dateAdhesion);
    const now = new Date();
    const diffMonths = (now.getFullYear() - adhesion.getFullYear()) * 12 + (now.getMonth() - adhesion.getMonth());
    return diffMonths >= 8;
  };

  // Auto-fill deces form when code_defunt is entered
  const handleCodeDefuntChange = (code) => {
    setDecesForm(prev => ({...prev, code_defunt: code}));

    if (!code) {
      setDecesForm(prev => ({
        ...prev,
        nom_defunt: '', type_defunt: '', id_adherent: '', beneficiaire: ''
      }));
      setPrestationEligible(true);
      return;
    }

    // Check if it's a principal
    const principal = principaux.find(p => p.code === code);
    if (principal) {
      // Vérifier si déjà décédé
      if (!principal.actif) {
        setError('Cet adhérent est déjà décédé (inactif)');
        setDecesForm(prev => ({
          ...prev,
          nom_defunt: '', type_defunt: '', id_adherent: '', beneficiaire: ''
        }));
        setPrestationEligible(true);
        return;
      }
      // Vérifier éligibilité prestation
      const eligible = checkPrestationEligibility(principal.date_adhesion);
      setPrestationEligible(eligible);
      if (!eligible) {
        setError('Attention: Adhérent non éligible à la prestation (moins de 8 mois d\'ancienneté)');
      }
      setDecesForm(prev => ({
        ...prev,
        nom_defunt: principal.nom || '',
        type_defunt: 'principal',
        id_adherent: principal.id,
        beneficiaire: principal.ayant_droit || ''
      }));
      return;
    }

    // Check if it's a secondaire
    const secondaire = secondaires.find(s => s.code === code);
    if (secondaire) {
      // Vérifier si déjà décédé
      if (!secondaire.actif) {
        setError('Cet adhérent est déjà décédé (inactif)');
        setDecesForm(prev => ({
          ...prev,
          nom_defunt: '', type_defunt: '', id_adherent: '', beneficiaire: ''
        }));
        setPrestationEligible(true);
        return;
      }
      // Vérifier éligibilité prestation
      const eligible = checkPrestationEligibility(secondaire.date_adhesion);
      setPrestationEligible(eligible);
      if (!eligible) {
        setError('Attention: Adhérent non éligible à la prestation (moins de 8 mois d\'ancienneté)');
      }
      const principalParent = principaux.find(p => p.id === secondaire.id_principal);
      setDecesForm(prev => ({
        ...prev,
        nom_defunt: secondaire.nom || '',
        type_defunt: 'secondaire',
        id_adherent: secondaire.id,
        beneficiaire: principalParent ? principalParent.nom : ''
      }));
    }
  };

  // Handle Restitution
  const handleRestitutionSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingRestitution) {
        await updateRestitution(editingRestitution.id, restitutionForm);
        setSuccess('Restitution mise à jour');
      } else {
        await createRestitution(restitutionForm);
        setSuccess('Restitution enregistrée');
      }
      resetRestitutionForm();
      loadRestitutions();
      if (selectedDeces) {
        loadDecesPaymentStatus(selectedDeces.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRestitution = async (id) => {
    if (!window.confirm('Supprimer cette restitution?')) return;
    try {
      await deleteRestitution(id);
      setSuccess('Restitution supprimée');
      loadRestitutions();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const resetRestitutionForm = () => {
    setRestitutionForm({
      id_deces: '', id_principal: '', montant_total: '', nb_personnes: 1, date_paiement: ''
    });
    setEditingRestitution(null);
    setShowRestitutionForm(false);
    setRestitutionPrincipalSearch('');
    setMontantDu(0);
    setMontantDejaPayé(0);
    setResteAPayer(0);
  };

  // Filtrer les principaux pour la restitution
  const filteredPrincipauxRestitution = principaux.filter(p => {
    if (!restitutionPrincipalSearch) return true;
    const search = restitutionPrincipalSearch.toLowerCase();
    return (
      p.code.toLowerCase().includes(search) ||
      p.nom.toLowerCase().includes(search) ||
      (p.contact && p.contact.toLowerCase().includes(search))
    );
  });

  // Filtrer les secondaires par leur adhérent principal
  const filteredSecondaires = secondaires.filter(s => {
    if (!secondairesPrincipalFilter) return true;
    const search = secondairesPrincipalFilter.toLowerCase();
    // Chercher le principal correspondant
    const principal = principaux.find(p => p.id === s.id_principal);
    if (!principal) return false;
    return (
      principal.code.toLowerCase().includes(search) ||
      principal.nom.toLowerCase().includes(search) ||
      (principal.contact && principal.contact.toLowerCase().includes(search)) ||
      (s.code_principal && s.code_principal.toLowerCase().includes(search))
    );
  });

  // Calculer automatiquement le montant de restitution
  const calculateRestitution = (decesId, principalId) => {
    if (!decesId || !principalId) {
      setMontantDu(0);
      setMontantDejaPayé(0);
      setResteAPayer(0);
      return;
    }

    const deces = decesList.find(d => d.id === parseInt(decesId));
    const principal = principaux.find(p => p.id === parseInt(principalId));

    if (!deces || !principal) {
      setMontantDu(0);
      setMontantDejaPayé(0);
      setResteAPayer(0);
      return;
    }

    const montantParTete = deces.montant_par_tete || 0;
    const dateDeces = new Date(deces.date_deces);

    // Compter les secondaires de ce principal
    const secondairesDuPrincipal = secondaires.filter(s => s.id_principal === parseInt(principalId));

    // Fonction pour vérifier si un adhérent a adhéré avant le décès
    const aAdhereAvantDeces = (dateAdhesion) => {
      if (!dateAdhesion) return false;
      const adhesion = new Date(dateAdhesion);
      return adhesion <= dateDeces;
    };

    // Fonction pour vérifier si un adhérent était actif à la date du décès
    const etaitActifALaDate = (adherentId, type) => {
      // Chercher si cet adhérent est décédé
      const decesAdherent = decesList.find(
        d => d.id_adherent === adherentId && d.type_defunt === type
      );

      if (!decesAdherent) {
        // Pas décédé dans la liste, vérifier le statut actif
        if (type === 'principal') {
          return principal.actif;
        } else {
          const sec = secondairesDuPrincipal.find(s => s.id === adherentId);
          return sec ? sec.actif : false;
        }
      }

      // L'adhérent est décédé, vérifier si c'était APRÈS le décès courant
      const dateDecesAdherent = new Date(decesAdherent.date_deces);
      return dateDecesAdherent > dateDeces;
    };

    let nbPersonnes = 0;

    // Le principal compte s'il a adhéré AVANT le décès ET était actif à la date du décès
    if (aAdhereAvantDeces(principal.date_adhesion) && (principal.actif || etaitActifALaDate(principal.id, 'principal'))) {
      nbPersonnes += 1;
    }

    // Compter les secondaires qui ont adhéré AVANT le décès ET étaient actifs à la date du décès
    secondairesDuPrincipal.forEach(s => {
      if (aAdhereAvantDeces(s.date_adhesion) && (s.actif || etaitActifALaDate(s.id, 'secondaire'))) {
        nbPersonnes += 1;
      }
    });

    const montantTotal = nbPersonnes * montantParTete;
    setMontantDu(montantTotal);

    // Calculer le montant déjà payé pour ce deces et ce principal
    const paiementsExistants = restitutions.filter(
      c => c.id_deces === parseInt(decesId) && c.id_principal === parseInt(principalId)
    );
    const totalDejaPayé = paiementsExistants.reduce((sum, c) => sum + Number(c.montant_total), 0);
    setMontantDejaPayé(totalDejaPayé);

    const reste = montantTotal - totalDejaPayé;
    setResteAPayer(reste > 0 ? reste : 0);

    setRestitutionForm(prev => ({
      ...prev,
      nb_personnes: nbPersonnes,
      montant_total: reste > 0 ? reste : ''
    }));
  };

  // Fonction de formatage des nombres pour PDF (avec points comme séparateurs)
  const formatNumberPDF = (num) => {
    return Number(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Export Dashboard PDF
  const exportDashboardPDF = () => {
    try {
      const doc = new jsPDF();
      const yearText = selectedYear ? `Année ${selectedYear}` : 'Toutes les années';

      // Titre
      doc.setFontSize(18);
      doc.text('ESSIE-EHOUN - Tableau de Bord', 14, 15);
      doc.setFontSize(12);
      doc.text(yearText, 14, 23);
      doc.setFontSize(10);
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 30);

      // Section Adhérents
      doc.setFontSize(14);
      doc.setTextColor(41, 128, 185);
      doc.text('Adhérents', 14, 42);
      doc.setTextColor(0, 0, 0);

      autoTable(doc, {
        startY: 46,
        head: [['Statistique', 'Valeur']],
        body: [
          ['Adhérents Principaux', dashboardStats.principaux.totalPrincipaux],
          ['Adhérents Secondaires', dashboardStats.principaux.totalSecondaires],
          ['Total Adhérents', dashboardStats.principaux.totalAdherents]
        ],
        styles: { fontSize: 10 },
        headStyles: { fillColor: [41, 128, 185] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } }
      });

      // Section Décès & Restitutions
      let currentY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.setTextColor(231, 76, 60);
      doc.text('Décès & Restitutions', 14, currentY);
      doc.setTextColor(0, 0, 0);

      autoTable(doc, {
        startY: currentY + 4,
        head: [['Statistique', 'Valeur']],
        body: [
          ['Décès Enregistrés', dashboardStats.deces.totalDeces],
          ['Total Restitutions', `${formatNumberPDF(dashboardStats.deces.totalCollecte)} FCFA`]
        ],
        styles: { fontSize: 10 },
        headStyles: { fillColor: [231, 76, 60] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } }
      });

      // Section Prestations
      currentY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.setTextColor(230, 126, 34);
      doc.text('Prestations', 14, currentY);
      doc.setTextColor(0, 0, 0);

      autoTable(doc, {
        startY: currentY + 4,
        head: [['Statistique', 'Valeur']],
        body: [
          ['Nombre de Prestations', dashboardStats.deces.totalPrestations || 0],
          ['Montant Total Prestations', `${formatNumberPDF(dashboardStats.deces.montantTotalPrestations || 0)} FCFA`]
        ],
        styles: { fontSize: 10 },
        headStyles: { fillColor: [230, 126, 34] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } }
      });

      // Résumé financier
      currentY = doc.lastAutoTable.finalY + 20;
      doc.setFontSize(12);
      doc.setTextColor(39, 174, 96);
      doc.text('Résumé Financier', 14, currentY);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);

      const totalCollecte = Number(dashboardStats.deces.totalCollecte);
      const totalPrestations = Number(dashboardStats.deces.montantTotalPrestations || 0);
      const solde = totalCollecte - totalPrestations;

      doc.text(`Total Collecté: ${formatNumberPDF(totalCollecte)} FCFA`, 14, currentY + 8);
      doc.text(`Total Prestations: ${formatNumberPDF(totalPrestations)} FCFA`, 14, currentY + 14);
      doc.setFontSize(11);
      doc.text(`Solde: ${formatNumberPDF(solde)} FCFA`, 14, currentY + 22);

      const fileName = selectedYear
        ? `tableau-bord-essie-${selectedYear}.pdf`
        : 'tableau-bord-essie-global.pdf';
      doc.save(fileName);
    } catch (err) {
      console.error('Erreur export PDF:', err);
      setError('Erreur lors de l\'export PDF: ' + err.message);
    }
  };

  // Export PDF Principaux
  const exportPrincipauxPDF = () => {
    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text('Liste des Adhérents Principaux - ESSIE-EHOUN', 14, 15);
    doc.setFontSize(10);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);
    doc.text(`Total: ${principaux.length} adhérents principaux`, 14, 28);

    autoTable(doc, {
      startY: 34,
      head: [['Code', 'Nom', 'Contact', 'Résidence', 'Date Adhésion', 'Secondaires', 'Statut']],
      body: principaux.map(p => [
        p.code,
        p.nom,
        p.contact || '',
        p.lieu_residence || '',
        p.date_adhesion ? new Date(p.date_adhesion).toLocaleDateString('fr-FR') : '-',
        getSecondairesDuPrincipal(p.id).length,
        p.actif ? 'Actif' : 'Inactif'
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        5: { halign: 'center' },
        6: { halign: 'center' }
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 6) {
          if (data.cell.raw === 'Inactif') {
            data.cell.styles.textColor = [231, 76, 60];
          } else {
            data.cell.styles.textColor = [39, 174, 96];
          }
        }
      }
    });

    doc.save('adherents-principaux-essie.pdf');
  };

  // Export PDF Secondaires
  const exportSecondairesPDF = () => {
    const doc = new jsPDF('landscape');
    doc.setFontSize(16);
    doc.text('Liste des Adhérents Secondaires - ESSIE-EHOUN', 14, 15);
    doc.setFontSize(10);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);

    const dataToExport = secondairesPrincipalFilter ? filteredSecondaires : secondaires;
    doc.text(`Total: ${dataToExport.length} adhérents secondaires${secondairesPrincipalFilter ? ` (filtre: "${secondairesPrincipalFilter}")` : ''}`, 14, 28);

    autoTable(doc, {
      startY: 34,
      head: [['Code', 'Principal', 'Nom', 'Lien Parenté', 'Résidence', 'Date Adhésion', 'Statut']],
      body: dataToExport.map(s => [
        s.code,
        s.code_principal,
        s.nom,
        s.lien_parente || '',
        s.lieu_residence || '',
        s.date_adhesion ? new Date(s.date_adhesion).toLocaleDateString('fr-FR') : '-',
        s.actif ? 'Actif' : 'Inactif'
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [46, 204, 113] },
      columnStyles: {
        6: { halign: 'center' }
      },
      didParseCell: function(data) {
        if (data.section === 'body' && data.column.index === 6) {
          if (data.cell.raw === 'Inactif') {
            data.cell.styles.textColor = [231, 76, 60];
          } else {
            data.cell.styles.textColor = [39, 174, 96];
          }
        }
      }
    });

    doc.save('adherents-secondaires-essie.pdf');
  };

  const exportDecesStatusPDF = () => { // eslint-disable-line no-unused-vars
    if (!decesPaymentStatus || !selectedDeces) {
      setError('Aucune donnée à exporter');
      return;
    }

    try {
      const doc = new jsPDF('landscape');
      doc.setFontSize(16);
      doc.text(`Statut des paiements - ${selectedDeces.nom_defunt}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Date: ${new Date(selectedDeces.date_deces).toLocaleDateString('fr-FR')} | Montant/tête: ${selectedDeces.montant_par_tete} FCFA`, 14, 22);

      const summary = decesPaymentStatus.summary;
      doc.text(`Payés: ${summary.totalPaye} | Partiels: ${summary.totalPartiel} | Non payés: ${summary.totalNonPaye}`, 14, 28);

      autoTable(doc, {
        startY: 34,
        head: [['Code', 'Nom', 'Contact', 'Nb Pers.', 'Montant Dû', 'Payé', 'Reste', 'Statut']],
        body: decesPaymentStatus.status.map(s => [
          s.code,
          s.nom,
          s.contact || '-',
          s.nb_personnes,
          `${formatNumberPDF(s.montant_du)} F`,
          `${formatNumberPDF(s.montant_paye)} F`,
          `${formatNumberPDF(s.montant_du - s.montant_paye)} F`,
          s.statut
        ]),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 50 },
          2: { cellWidth: 30 },
          3: { cellWidth: 18 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 25 },
          7: { cellWidth: 22 }
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 7) {
            if (data.cell.raw === 'Payé') {
              data.cell.styles.fillColor = [39, 174, 96];
              data.cell.styles.textColor = 255;
            } else if (data.cell.raw === 'Partiel') {
              data.cell.styles.fillColor = [243, 156, 18];
              data.cell.styles.textColor = 255;
            } else if (data.cell.raw === 'Non payé') {
              data.cell.styles.fillColor = [231, 76, 60];
              data.cell.styles.textColor = 255;
            }
          }
        }
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      const totalDu = decesPaymentStatus.status.reduce((sum, s) => sum + s.montant_du, 0);
      const totalPaye = decesPaymentStatus.status.reduce((sum, s) => sum + s.montant_paye, 0);
      doc.setFontSize(10);
      doc.text(`Total dû: ${formatNumberPDF(totalDu)} FCFA | Total payé: ${formatNumberPDF(totalPaye)} FCFA | Reste: ${formatNumberPDF(totalDu - totalPaye)} FCFA`, 14, finalY);

      doc.save(`statut-paiements-${selectedDeces.nom_defunt}.pdf`);
    } catch (err) {
      console.error('Erreur export PDF:', err);
      setError('Erreur lors de l\'export PDF: ' + err.message);
    }
  };

  // Render pagination
  const renderPagination = (pagination, loadFunction) => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      if (i === 1 || i === pagination.totalPages ||
          (i >= pagination.page - 2 && i <= pagination.page + 2)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }

    return (
      <div className="pagination">
        <button
          onClick={() => loadFunction(pagination.page - 1)}
          disabled={pagination.page === 1}
        >
          Préc.
        </button>
        {pages.map((p, i) => (
          p === '...' ? (
            <span key={i}>...</span>
          ) : (
            <button
              key={i}
              onClick={() => loadFunction(p)}
              className={pagination.page === p ? 'active' : ''}
            >
              {p}
            </button>
          )
        ))}
        <button
          onClick={() => loadFunction(pagination.page + 1)}
          disabled={pagination.page === pagination.totalPages}
        >
          Suiv.
        </button>
      </div>
    );
  };

  return (
    <div className="ara-module">
      <h1>Module ESSIE-EHOUN</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="ara-tabs">
        <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
          Tableau de bord
        </button>
        <button className={activeTab === 'principaux' ? 'active' : ''} onClick={() => setActiveTab('principaux')}>
          Adhérents Principaux
        </button>
        <button className={activeTab === 'secondaires' ? 'active' : ''} onClick={() => setActiveTab('secondaires')}>
          Adhérents Secondaires
        </button>
        <button className={activeTab === 'deces' ? 'active' : ''} onClick={() => setActiveTab('deces')}>
          Décès
        </button>
        <button className={activeTab === 'restitutions' ? 'active' : ''} onClick={() => setActiveTab('restitutions')}>
          Restitutions
        </button>
        <button className={activeTab === 'statut-paiements' ? 'active' : ''} onClick={() => setActiveTab('statut-paiements')}>
          Statut Paiements
        </button>
        <button className={activeTab === 'sms-broadcast' ? 'active' : ''} onClick={() => setActiveTab('sms-broadcast')}>
          Envoi SMS Groupé
        </button>
      </div>

      {loading && <div className="loading">Chargement...</div>}

      {/* Dashboard */}
      {activeTab === 'dashboard' && (
        <div className="dashboard">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px'}}>
            <h2 style={{margin: 0}}>Tableau de Bord ESSIE-EHOUN</h2>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <label style={{fontWeight: 'bold'}}>Année:</label>
              <select
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', minWidth: '150px'}}
              >
                <option value={new Date().getFullYear()}>{new Date().getFullYear()} (en cours)</option>
                {dashboardStats.deces.availableYears && dashboardStats.deces.availableYears
                  .filter(year => year !== new Date().getFullYear())
                  .map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))
                }
                <option value="">Toutes les années</option>
              </select>
              <button className="btn-export" onClick={exportDashboardPDF} style={{marginLeft: '10px'}}>
                Exporter PDF
              </button>
            </div>
          </div>

          <h3 style={{marginBottom: '15px', color: '#333'}}>Adhérents</h3>
          <div className="stats-grid">
            <div className="stat-card blue">
              <h3>Adhérents Principaux</h3>
              <p className="stat-number">{dashboardStats.principaux.totalPrincipaux}</p>
            </div>
            <div className="stat-card green">
              <h3>Adhérents Secondaires</h3>
              <p className="stat-number">{dashboardStats.principaux.totalSecondaires}</p>
            </div>
            <div className="stat-card purple">
              <h3>Total Adhérents</h3>
              <p className="stat-number">{dashboardStats.principaux.totalAdherents}</p>
            </div>
          </div>

          <h3 style={{marginTop: '30px', marginBottom: '15px', color: '#333'}}>Adhésions {selectedYear ? `(${selectedYear})` : '(toutes années)'}</h3>
          <div className="stats-grid">
            <div className="stat-card teal">
              <h3>Nouveaux Adhérents</h3>
              <p className="stat-number">{dashboardStats.principaux.nouveauxAdherents ?? 0}</p>
            </div>
            <div className="stat-card orange">
              <h3>Montant Total Adhésion</h3>
              <p className="stat-number">{Number(dashboardStats.principaux.montantTotalAdhesion || 0).toLocaleString()} F</p>
            </div>
          </div>

          <h3 style={{marginTop: '30px', marginBottom: '15px', color: '#333'}}>Décès & Restitutions</h3>
          <div className="stats-grid">
            <div className="stat-card red">
              <h3>Décès Enregistrés</h3>
              <p className="stat-number">{dashboardStats.deces.totalDeces}</p>
            </div>
            <div className="stat-card yellow">
              <h3>Total Restitutions</h3>
              <p className="stat-number">{Number(dashboardStats.deces.totalCollecte).toLocaleString()} F</p>
            </div>
          </div>

          <h3 style={{marginTop: '30px', marginBottom: '15px', color: '#333'}}>Prestations</h3>
          <div className="stats-grid">
            <div className="stat-card orange">
              <h3>Nombre de Prestations</h3>
              <p className="stat-number">{dashboardStats.deces.totalPrestations || 0}</p>
            </div>
            <div className="stat-card teal">
              <h3>Montant Total Prestations</h3>
              <p className="stat-number">{Number(dashboardStats.deces.montantTotalPrestations || 0).toLocaleString()} F</p>
            </div>
          </div>
        </div>
      )}

      {/* Principaux */}
      {activeTab === 'principaux' && (
        <div className="ara-content">
          <div className="section-header">
            <button className="btn-primary" onClick={() => setShowPrincipalForm(true)}>
              + Nouvel Adhérent
            </button>
            <button className="btn-export" onClick={exportPrincipauxPDF}>
              Exporter PDF
            </button>
          </div>

          {showPrincipalForm && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>{editingPrincipal ? 'Modifier' : 'Nouvel'} Adhérent Principal</h3>
                <form onSubmit={handlePrincipalSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Code *</label>
                      <input
                        type="text"
                        value={principalForm.code}
                        onChange={(e) => setPrincipalForm({...principalForm, code: e.target.value})}
                        required
                        disabled={editingPrincipal}
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom *</label>
                      <input
                        type="text"
                        value={principalForm.nom}
                        onChange={(e) => setPrincipalForm({...principalForm, nom: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Date d'adhésion</label>
                      <input
                        type="date"
                        value={principalForm.date_adhesion}
                        onChange={(e) => setPrincipalForm({...principalForm, date_adhesion: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Montant adhésion (FCFA)</label>
                      <input
                        type="number"
                        value={principalForm.montant_adhesion}
                        onChange={(e) => setPrincipalForm({...principalForm, montant_adhesion: e.target.value})}
                        placeholder="Ex: 10000"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Contact</label>
                      <input
                        type="text"
                        value={principalForm.contact}
                        onChange={(e) => setPrincipalForm({...principalForm, contact: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Lieu de résidence</label>
                      <input
                        type="text"
                        value={principalForm.lieu_residence}
                        onChange={(e) => setPrincipalForm({...principalForm, lieu_residence: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Ayant droit</label>
                      <input
                        type="text"
                        value={principalForm.ayant_droit}
                        onChange={(e) => setPrincipalForm({...principalForm, ayant_droit: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact ayant droit</label>
                      <input
                        type="text"
                        value={principalForm.contact_ayant_droit}
                        onChange={(e) => setPrincipalForm({...principalForm, contact_ayant_droit: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Statut</label>
                      <select
                        value={principalForm.actif ? 'actif' : 'inactif'}
                        onChange={(e) => setPrincipalForm({...principalForm, actif: e.target.value === 'actif'})}
                      >
                        <option value="actif">Actif</option>
                        <option value="inactif">Inactif (Décès)</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">Enregistrer</button>
                    <button type="button" className="btn-export" onClick={resetPrincipalForm}>Annuler</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <table className="ara-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Nom</th>
                <th>Contact</th>
                <th>Résidence</th>
                <th>Date Adhésion</th>
                <th>Mnt. Adhésion</th>
                <th>Secondaires</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {principaux.map(p => (
                <tr key={p.id} className={!p.actif ? 'row-unpaid' : ''}>
                  <td>{p.code}</td>
                  <td>{p.nom}</td>
                  <td>{p.contact}</td>
                  <td>{p.lieu_residence}</td>
                  <td>{p.date_adhesion ? new Date(p.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                  <td>{p.montant_adhesion ? `${Number(p.montant_adhesion).toLocaleString()} F` : '-'}</td>
                  <td style={{textAlign: 'center'}}>
                    <button
                      className="btn-view"
                      onClick={() => setViewingPrincipalSecondaires(p)}
                      style={{padding: '4px 8px', fontSize: '12px'}}
                    >
                      {getSecondairesDuPrincipal(p.id).length}
                    </button>
                  </td>
                  <td>
                    <span className={`status-badge ${p.actif ? 'payé' : 'non-payé'}`}>
                      {p.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-edit" onClick={() => editPrincipal(p)}>Modifier</button>
                    <button className="btn-delete" onClick={() => handleDeletePrincipal(p.id)}>Supprimer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Modal pour voir les secondaires d'un principal */}
          {viewingPrincipalSecondaires && (
            <div className="modal-overlay">
              <div className="modal" style={{maxWidth: '800px'}}>
                <h3>Adhérents Secondaires de {viewingPrincipalSecondaires.code} - {viewingPrincipalSecondaires.nom}</h3>

                {getSecondairesDuPrincipal(viewingPrincipalSecondaires.id).length === 0 ? (
                  <p style={{color: '#666', fontStyle: 'italic', padding: '20px 0'}}>Aucun adhérent secondaire pour ce principal</p>
                ) : (
                  <table className="ara-table" style={{marginTop: '15px'}}>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Nom</th>
                        <th>Lien de parenté</th>
                        <th>Date Adhésion</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSecondairesDuPrincipal(viewingPrincipalSecondaires.id).map(s => (
                        <tr key={s.id} className={!s.actif ? 'row-unpaid' : ''}>
                          <td>{s.code}</td>
                          <td>{s.nom}</td>
                          <td>{s.lien_parente}</td>
                          <td>{s.date_adhesion ? new Date(s.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                          <td>
                            <span className={`status-badge ${s.actif ? 'payé' : 'non-payé'}`}>
                              {s.actif ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{marginTop: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                  <strong>Total: {getSecondairesDuPrincipal(viewingPrincipalSecondaires.id).length} secondaire(s)</strong>
                  {' | '}
                  Actifs: {getSecondairesDuPrincipal(viewingPrincipalSecondaires.id).filter(s => s.actif).length}
                  {' | '}
                  Inactifs: {getSecondairesDuPrincipal(viewingPrincipalSecondaires.id).filter(s => !s.actif).length}
                </div>

                <div className="form-actions" style={{marginTop: '20px'}}>
                  <button className="btn-export" onClick={() => setViewingPrincipalSecondaires(null)}>Fermer</button>
                </div>
              </div>
            </div>
          )}

          {renderPagination(principauxPagination, loadPrincipaux)}
        </div>
      )}

      {/* Secondaires */}
      {activeTab === 'secondaires' && (
        <div className="ara-content">
          <div className="section-header">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <button className="btn-primary" onClick={() => setShowSecondaireForm(true)}>
                + Nouvel Adhérent Secondaire
              </button>
              <button className="btn-export" onClick={exportSecondairesPDF}>
                Exporter PDF
              </button>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <input
                type="text"
                placeholder="Rechercher par adhérent principal (code, nom, contact)..."
                value={secondairesPrincipalFilter}
                onChange={(e) => setSecondairesPrincipalFilter(e.target.value)}
                style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', width: '350px'}}
              />
              {secondairesPrincipalFilter && (
                <button
                  className="btn-export"
                  onClick={() => setSecondairesPrincipalFilter('')}
                  style={{padding: '8px 12px'}}
                >
                  Effacer
                </button>
              )}
            </div>
          </div>

          {showSecondaireForm && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>{editingSecondaire ? 'Modifier' : 'Nouvel'} Adhérent Secondaire</h3>
                <form onSubmit={handleSecondaireSubmit}>
                  <div className="form-group">
                    <label>Adhérent Principal *</label>
                    <input
                      type="text"
                      placeholder="Rechercher par code, nom, prénoms ou contact..."
                      value={principalSearch}
                      onChange={(e) => setPrincipalSearch(e.target.value)}
                      style={{marginBottom: '8px'}}
                    />
                    <select
                      value={secondaireForm.id_principal}
                      onChange={(e) => setSecondaireForm({...secondaireForm, id_principal: e.target.value})}
                      required
                      size={5}
                      style={{width: '100%'}}
                    >
                      <option value="">Sélectionner...</option>
                      {filteredPrincipaux.map(p => (
                        <option key={p.id} value={p.id}>{p.code} - {p.nom} {p.contact ? `(${p.contact})` : ''}</option>
                      ))}
                    </select>
                    <small style={{color: '#666'}}>{filteredPrincipaux.length} résultat(s)</small>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nom *</label>
                      <input
                        type="text"
                        value={secondaireForm.nom}
                        onChange={(e) => setSecondaireForm({...secondaireForm, nom: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Lien de parenté</label>
                      <select
                        value={secondaireForm.lien_parente}
                        onChange={(e) => setSecondaireForm({...secondaireForm, lien_parente: e.target.value})}
                      >
                        <option value="">-- Non renseigné --</option>
                        <option value="père">Père</option>
                        <option value="mère">Mère</option>
                        <option value="fils">Fils</option>
                        <option value="fille">Fille</option>
                        <option value="parent">Parent</option>
                        <option value="conjoint">Conjoint</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Date d'adhésion</label>
                      <input
                        type="date"
                        value={secondaireForm.date_adhesion}
                        onChange={(e) => setSecondaireForm({...secondaireForm, date_adhesion: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Montant adhésion (FCFA)</label>
                      <input
                        type="number"
                        value={secondaireForm.montant_adhesion}
                        onChange={(e) => setSecondaireForm({...secondaireForm, montant_adhesion: e.target.value})}
                        placeholder="Ex: 10000"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Lieu de résidence</label>
                      <input
                        type="text"
                        value={secondaireForm.lieu_residence}
                        onChange={(e) => setSecondaireForm({...secondaireForm, lieu_residence: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Statut</label>
                      <select
                        value={secondaireForm.actif ? 'actif' : 'inactif'}
                        onChange={(e) => setSecondaireForm({...secondaireForm, actif: e.target.value === 'actif'})}
                      >
                        <option value="actif">Actif</option>
                        <option value="inactif">Inactif (Décès)</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">Enregistrer</button>
                    <button type="button" className="btn-export" onClick={resetSecondaireForm}>Annuler</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {secondairesPrincipalFilter && (
            <div style={{marginBottom: '10px', color: '#666'}}>
              {filteredSecondaires.length} résultat(s) trouvé(s) pour "{secondairesPrincipalFilter}"
            </div>
          )}

          <table className="ara-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Principal</th>
                <th>Nom</th>
                <th>Lien</th>
                <th>Date Adhésion</th>
                <th>Mnt. Adhésion</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSecondaires.map(s => (
                <tr key={s.id} className={!s.actif ? 'row-unpaid' : ''}>
                  <td>{s.code}</td>
                  <td>{s.code_principal}</td>
                  <td>{s.nom}</td>
                  <td>{s.lien_parente}</td>
                  <td>{s.date_adhesion ? new Date(s.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                  <td>{s.montant_adhesion ? `${Number(s.montant_adhesion).toLocaleString()} F` : '-'}</td>
                  <td>
                    <span className={`status-badge ${s.actif ? 'payé' : 'non-payé'}`}>
                      {s.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-edit" onClick={() => editSecondaire(s)}>Modifier</button>
                    <button className="btn-delete" onClick={() => handleDeleteSecondaire(s.id)}>Supprimer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!secondairesPrincipalFilter && renderPagination(secondairesPagination, loadSecondaires)}
        </div>
      )}

      {/* Deces */}
      {activeTab === 'deces' && (
        <div className="ara-content">
          <div className="section-header">
            <button className="btn-primary" onClick={() => setShowDecesForm(true)}>
              + Nouveau Décès
            </button>
          </div>

          {showDecesForm && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>{editingDeces ? 'Modifier' : 'Nouveau'} Décès</h3>
                <form onSubmit={handleDecesSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Code *</label>
                      <input
                        type="text"
                        value={decesForm.code_defunt}
                        onChange={(e) => handleCodeDefuntChange(e.target.value)}
                        required
                        placeholder="Entrez le code de l'adhérent"
                      />
                      <small style={{color: '#666'}}>Les champs seront remplis automatiquement</small>
                    </div>
                    <div className="form-group">
                      <label>Nom du défunt *</label>
                      <input
                        type="text"
                        value={decesForm.nom_defunt}
                        onChange={(e) => setDecesForm({...decesForm, nom_defunt: e.target.value})}
                        required
                        readOnly={!!decesForm.type_defunt}
                        style={decesForm.type_defunt ? {backgroundColor: '#f5f5f5'} : {}}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>N° Cas</label>
                      <input
                        type="text"
                        value={decesForm.numero_cas}
                        onChange={(e) => setDecesForm({...decesForm, numero_cas: e.target.value})}
                        placeholder="Ex: 10e cas"
                      />
                    </div>
                    <div className="form-group">
                      <label>Date du décès</label>
                      <input
                        type="date"
                        value={decesForm.date_deces}
                        onChange={(e) => setDecesForm({...decesForm, date_deces: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Montant par tête (FCFA) *</label>
                      <input
                        type="number"
                        value={decesForm.montant_par_tete}
                        onChange={(e) => setDecesForm({...decesForm, montant_par_tete: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={decesForm.description}
                      onChange={(e) => setDecesForm({...decesForm, description: e.target.value})}
                      rows="3"
                    />
                  </div>

                  <h4 style={{margin: '20px 0 15px', borderTop: '1px solid #e0e0e0', paddingTop: '15px'}}>
                    Prestation
                    {!prestationEligible && decesForm.type_defunt && (
                      <span style={{color: '#dc3545', fontSize: '12px', marginLeft: '10px'}}>
                        (Non éligible - moins de 8 mois d'ancienneté)
                      </span>
                    )}
                  </h4>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Type de défunt</label>
                      <input
                        type="text"
                        value={decesForm.type_defunt ? (decesForm.type_defunt === 'principal' ? 'Principal' : 'Secondaire') : ''}
                        readOnly
                        style={{backgroundColor: '#f5f5f5'}}
                        placeholder="Auto-rempli par le code"
                      />
                    </div>
                    <div className="form-group">
                      <label>Montant prestation (FCFA)</label>
                      <input
                        type="number"
                        value={decesForm.montant_prestation}
                        onChange={(e) => setDecesForm({...decesForm, montant_prestation: e.target.value})}
                        disabled={!prestationEligible}
                        style={!prestationEligible ? {backgroundColor: '#f5f5f5'} : {}}
                      />
                    </div>
                    <div className="form-group">
                      <label>Bénéficiaire</label>
                      <input
                        type="text"
                        value={decesForm.beneficiaire}
                        onChange={(e) => setDecesForm({...decesForm, beneficiaire: e.target.value})}
                        disabled={!prestationEligible}
                        style={!prestationEligible ? {backgroundColor: '#f5f5f5'} : {}}
                        placeholder="Auto-rempli par le code"
                      />
                      <small style={{color: '#666'}}>
                        {decesForm.type_defunt === 'principal' ? "L'ayant droit du principal" :
                         decesForm.type_defunt === 'secondaire' ? "L'adhérent principal" : ''}
                      </small>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Date prestation</label>
                      <input
                        type="date"
                        value={decesForm.date_prestation}
                        onChange={(e) => setDecesForm({...decesForm, date_prestation: e.target.value})}
                        disabled={!prestationEligible}
                        style={!prestationEligible ? {backgroundColor: '#f5f5f5'} : {}}
                      />
                    </div>
                    <div className="form-group">
                      <label>Prestation payée</label>
                      <div style={{paddingTop: '8px'}}>
                        <input
                          type="checkbox"
                          checked={decesForm.prestation_payee}
                          onChange={(e) => setDecesForm({...decesForm, prestation_payee: e.target.checked})}
                          style={{width: 'auto', marginRight: '8px'}}
                          disabled={!prestationEligible}
                        />
                        <span>{decesForm.prestation_payee ? 'Oui' : 'Non'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-primary">Enregistrer</button>
                    <button type="button" className="btn-export" onClick={resetDecesForm}>Annuler</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <table className="ara-table">
            <thead>
              <tr>
                <th>N° Cas</th>
                <th>Code</th>
                <th>Date</th>
                <th>Nom</th>
                <th>Montant/Tête</th>
                <th>Collecté</th>
                <th>Prestation</th>
                <th>Bénéficiaire</th>
                <th>Statut Prest.</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {decesList.map(d => (
                <tr key={d.id}>
                  <td><strong>{d.numero_cas || '-'}</strong></td>
                  <td>{d.code_defunt}</td>
                  <td>{d.date_deces ? new Date(d.date_deces).toLocaleDateString('fr-FR') : ''}</td>
                  <td>{d.nom_defunt}</td>
                  <td>{Number(d.montant_par_tete).toLocaleString()} F</td>
                  <td>{Number(d.montant_collecte || 0).toLocaleString()} F</td>
                  <td>{d.montant_prestation ? `${Number(d.montant_prestation).toLocaleString()} F` : '-'}</td>
                  <td>{d.beneficiaire || '-'}</td>
                  <td>
                    {d.montant_prestation ? (
                      <span className={`status-badge ${d.prestation_payee ? 'payé' : 'non-payé'}`}>
                        {d.prestation_payee ? 'Payé' : 'Non payé'}
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    <button className="btn-view" onClick={() => { loadDecesPaymentStatus(d.id); setActiveTab('statut-paiements'); }}>Statut</button>
                    <button className="btn-primary" onClick={() => handleSendDecesSMS(d)} style={{backgroundColor: '#17a2b8', marginRight: '5px'}}>SMS</button>
                    <button className="btn-edit" onClick={() => editDecesItem(d)}>Modifier</button>
                    <button className="btn-delete" onClick={() => handleDeleteDeces(d.id)}>Supprimer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination(decesPagination, loadDeces)}
        </div>
      )}

      {/* Restitutions */}
      {activeTab === 'restitutions' && (
        <div className="ara-content">
          <div className="section-header">
            <button className="btn-primary" onClick={() => setShowRestitutionForm(true)}>
              + Nouvelle Restitution
            </button>
          </div>

          {showRestitutionForm && (
            <div className="modal-overlay">
              <div className="modal">
                <h3>{editingRestitution ? 'Modifier' : 'Nouvelle'} Restitution</h3>
                <form onSubmit={handleRestitutionSubmit}>
                  <div className="form-group">
                    <label>Décès *</label>
                    <select
                      value={restitutionForm.id_deces}
                      onChange={(e) => {
                        const newDecesId = e.target.value;
                        setRestitutionForm({...restitutionForm, id_deces: newDecesId});
                        calculateRestitution(newDecesId, restitutionForm.id_principal);
                      }}
                      required
                    >
                      <option value="">Sélectionner...</option>
                      {decesList.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.nom_defunt} - {d.date_deces ? new Date(d.date_deces).toLocaleDateString('fr-FR') : ''} ({Number(d.montant_par_tete).toLocaleString()} F/tête)
                        </option>
                      ))}
                    </select>
                    {restitutionForm.id_deces && (
                      <small style={{color: '#007bff'}}>
                        Montant par tête: {Number(decesList.find(d => d.id === parseInt(restitutionForm.id_deces))?.montant_par_tete || 0).toLocaleString()} FCFA
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Adhérent Principal *</label>
                    <input
                      type="text"
                      placeholder="Rechercher par code, nom, prénoms ou contact..."
                      value={restitutionPrincipalSearch}
                      onChange={(e) => setRestitutionPrincipalSearch(e.target.value)}
                      style={{marginBottom: '8px'}}
                    />
                    <select
                      value={restitutionForm.id_principal}
                      onChange={(e) => {
                        const newPrincipalId = e.target.value;
                        setRestitutionForm({...restitutionForm, id_principal: newPrincipalId});
                        calculateRestitution(restitutionForm.id_deces, newPrincipalId);
                      }}
                      required
                      size={5}
                      style={{width: '100%'}}
                    >
                      <option value="">Sélectionner...</option>
                      {filteredPrincipauxRestitution.map(p => (
                        <option key={p.id} value={p.id}>{p.code} - {p.nom} {p.contact ? `(${p.contact})` : ''}</option>
                      ))}
                    </select>
                    <small style={{color: '#666'}}>{filteredPrincipauxRestitution.length} résultat(s)</small>
                  </div>
                  {resteAPayer === 0 && montantDu > 0 && (
                    <div className="alert alert-success" style={{marginBottom: '15px'}}>
                      Ce paiement est déjà soldé ({montantDejaPayé.toLocaleString()} F payés sur {montantDu.toLocaleString()} F)
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Nb personnes</label>
                      <input
                        type="number"
                        value={restitutionForm.nb_personnes}
                        readOnly
                        style={{backgroundColor: '#f5f5f5'}}
                      />
                      <small style={{color: '#666'}}>Actifs uniquement</small>
                    </div>
                    <div className="form-group">
                      <label>Montant dû</label>
                      <input
                        type="text"
                        value={`${montantDu.toLocaleString()} F`}
                        readOnly
                        style={{backgroundColor: '#f5f5f5'}}
                      />
                    </div>
                    <div className="form-group">
                      <label>Déjà payé</label>
                      <input
                        type="text"
                        value={`${montantDejaPayé.toLocaleString()} F`}
                        readOnly
                        style={{backgroundColor: montantDejaPayé > 0 ? '#fff3cd' : '#f5f5f5'}}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reste à payer</label>
                      <input
                        type="text"
                        value={`${resteAPayer.toLocaleString()} F`}
                        readOnly
                        style={{backgroundColor: resteAPayer === 0 ? '#d4edda' : '#f8d7da', fontWeight: 'bold'}}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Montant à verser (FCFA) *</label>
                    <input
                      type="number"
                      value={restitutionForm.montant_total}
                      onChange={(e) => setRestitutionForm({...restitutionForm, montant_total: e.target.value})}
                      required
                      disabled={resteAPayer === 0 && montantDu > 0}
                      max={resteAPayer}
                      style={{fontWeight: 'bold', fontSize: '16px'}}
                    />
                    {resteAPayer > 0 && restitutionForm.montant_total && Number(restitutionForm.montant_total) > resteAPayer && (
                      <small style={{color: '#dc3545'}}>
                        Le montant dépasse le reste à payer
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Date de paiement</label>
                    <input
                      type="date"
                      value={restitutionForm.date_paiement}
                      onChange={(e) => setRestitutionForm({...restitutionForm, date_paiement: e.target.value})}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn-primary">Enregistrer</button>
                    <button type="button" className="btn-export" onClick={resetRestitutionForm}>Annuler</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <table className="ara-table">
            <thead>
              <tr>
                <th>N° Cas</th>
                <th>Date</th>
                <th>Défunt</th>
                <th>Adhérent</th>
                <th>Nb Pers.</th>
                <th>Montant</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {restitutions.map(c => (
                <tr key={c.id}>
                  <td><strong>{c.numero_cas || '-'}</strong></td>
                  <td>{c.date_paiement ? new Date(c.date_paiement).toLocaleDateString('fr-FR') : ''}</td>
                  <td>{c.nom_defunt}</td>
                  <td>{c.code_principal} - {c.nom_principal}</td>
                  <td>{c.nb_personnes}</td>
                  <td>{Number(c.montant_total).toLocaleString()} F</td>
                  <td>
                    <button className="btn-delete" onClick={() => handleDeleteRestitution(c.id)}>Supprimer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination(restitutionsPagination, loadRestitutions)}
        </div>
      )}

      {/* Statut des Paiements - Liste des adhérents avec détails */}
      {activeTab === 'statut-paiements' && (
        <div className="section">
          <div className="section-header">
            <h2>Liste des Adhérents - Statut des Restitutions</h2>
          </div>

          {adherentsPaymentDetails && (
            <>
              <div className="stats-grid" style={{marginBottom: '20px'}}>
                <div className="stat-card green">
                  <h3>{adherentsPaymentDetails.summary.aJour}</h3>
                  <p>À jour</p>
                </div>
                <div className="stat-card orange">
                  <h3>{adherentsPaymentDetails.summary.enRetard}</h3>
                  <p>En retard</p>
                </div>
                <div className="stat-card red">
                  <h3>{adherentsPaymentDetails.summary.nonPaye}</h3>
                  <p>Non payé</p>
                </div>
                <div className="stat-card blue">
                  <h3>{Number(adherentsPaymentDetails.summary.totalPaye).toLocaleString()} F</h3>
                  <p>Total Collecté</p>
                </div>
              </div>

              <table className="ara-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Nom</th>
                    <th>Contact</th>
                    <th style={{textAlign: 'right'}}>Total Dû</th>
                    <th style={{textAlign: 'right'}}>Total Payé</th>
                    <th style={{textAlign: 'right'}}>Reste</th>
                    <th style={{textAlign: 'center'}}>Statut</th>
                    <th>Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {adherentsPaymentDetails.adherents.map(a => (
                    <React.Fragment key={a.id}>
                      <tr className={a.statut_global === 'À jour' ? 'row-paid' : a.statut_global === 'Non payé' ? 'row-unpaid' : ''}>
                        <td><strong>{a.code}</strong></td>
                        <td>{a.nom}</td>
                        <td>{a.contact || '-'}</td>
                        <td style={{textAlign: 'right'}}>{Number(a.total_du).toLocaleString()} F</td>
                        <td style={{textAlign: 'right', fontWeight: 'bold'}}>{Number(a.total_paye).toLocaleString()} F</td>
                        <td style={{textAlign: 'right', color: a.reste > 0 ? '#dc3545' : '#28a745', fontWeight: 'bold'}}>
                          {Number(a.reste).toLocaleString()} F
                        </td>
                        <td style={{textAlign: 'center'}}>
                          <span className={`status-badge ${a.statut_global === 'À jour' ? 'payé' : a.statut_global === 'Non payé' ? 'non-payé' : 'partiel'}`}>
                            {a.statut_global}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-view"
                            onClick={() => setExpandedAdherent(expandedAdherent === a.id ? null : a.id)}
                          >
                            {expandedAdherent === a.id ? 'Masquer' : 'Voir'}
                          </button>
                        </td>
                      </tr>
                      {expandedAdherent === a.id && (
                        <tr>
                          <td colSpan="8" style={{padding: '15px', backgroundColor: '#f8f9fa'}}>
                            <h4 style={{margin: '0 0 10px 0', color: '#333'}}>Détail des restitutions par décès</h4>
                            {a.restitutions.length === 0 ? (
                              <p style={{color: '#666', fontStyle: 'italic'}}>Aucune restitution due</p>
                            ) : (
                              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead>
                                  <tr style={{backgroundColor: '#e9ecef'}}>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6'}}>N° Cas</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6'}}>Défunt</th>
                                    <th style={{padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6'}}>Date</th>
                                    <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #dee2e6'}}>Nb Pers.</th>
                                    <th style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid #dee2e6'}}>Dû</th>
                                    <th style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid #dee2e6'}}>Payé</th>
                                    <th style={{padding: '8px', textAlign: 'right', borderBottom: '1px solid #dee2e6'}}>Reste</th>
                                    <th style={{padding: '8px', textAlign: 'center', borderBottom: '1px solid #dee2e6'}}>Statut</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {a.restitutions.map(c => (
                                    <tr key={c.deces_id} style={{borderBottom: '1px solid #dee2e6'}}>
                                      <td style={{padding: '8px', fontWeight: 'bold'}}>{c.numero_cas || '-'}</td>
                                      <td style={{padding: '8px'}}>{c.code_defunt} - {c.nom_defunt}</td>
                                      <td style={{padding: '8px'}}>{new Date(c.date_deces).toLocaleDateString('fr-FR')}</td>
                                      <td style={{padding: '8px', textAlign: 'center'}}>{c.nb_personnes}</td>
                                      <td style={{padding: '8px', textAlign: 'right'}}>{Number(c.montant_du).toLocaleString()} F</td>
                                      <td style={{padding: '8px', textAlign: 'right'}}>{Number(c.montant_paye).toLocaleString()} F</td>
                                      <td style={{padding: '8px', textAlign: 'right', color: c.reste > 0 ? '#dc3545' : '#28a745'}}>
                                        {Number(c.reste).toLocaleString()} F
                                      </td>
                                      <td style={{padding: '8px', textAlign: 'center'}}>
                                        <span className={`status-badge ${c.statut.toLowerCase().replace(' ', '-')}`}>{c.statut}</span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              <div style={{marginTop: '20px', color: '#666', fontSize: '14px'}}>
                Total: {adherentsPaymentDetails.summary.total} adhérents |
                Total dû: <strong>{Number(adherentsPaymentDetails.summary.totalDu).toLocaleString()} F</strong> |
                Total payé: <strong>{Number(adherentsPaymentDetails.summary.totalPaye).toLocaleString()} F</strong> |
                Reste: <strong style={{color: '#dc3545'}}>{Number(adherentsPaymentDetails.summary.totalDu - adherentsPaymentDetails.summary.totalPaye).toLocaleString()} F</strong>
              </div>
            </>
          )}
        </div>
      )}

      {/* Envoi SMS Groupé */}
      {activeTab === 'sms-broadcast' && (
        <div className="section">
          <BroadcastSMS />
        </div>
      )}
    </div>
  );
};

export default ESSIEModule;
